import { createContext, useContext } from 'react';

import { TamaguiProvider, TamaguiProviderProps } from '@tamagui/core';
import { PortalProvider } from '@tamagui/portal';
import { ToastProvider, ToastViewport, ToastViewportProps } from '@tamagui/toast';

import { EventType } from './helpers/analytics';
import config from './tamagui.config';

export interface IAnalyticsContext {
  useObservability: () => {
    trackJourneyEvent: (
      categoryName: 'platform',
      eventName: EventType,
      eventData: { targetName: string },
      contextualData?: object,
    ) => void;
  };
}

export const defaultAnalyticsContext: IAnalyticsContext = {
  useObservability: () => {
    return {
      trackJourneyEvent: (
        _categoryName: 'platform',
        _eventName: EventType,
        _eventData: { targetName: string },
        _contextualData?: object,
      ) => {},
    };
  },
};

export const AnalyticsContext = createContext<IAnalyticsContext>(defaultAnalyticsContext);

/**
 * Hook for using the AnalyticsContext context
 */
export const useAnalyticsContext = () => useContext(AnalyticsContext);

// TODO: Decide if/how we want to allow bricks consumers to override config.
export type BricksProviderProps = Omit<TamaguiProviderProps, 'config'> & {
  useObservability: IAnalyticsContext['useObservability'];
  toastViewportProps?: ToastViewportProps;
};

export function BricksProvider({
  children,
  useObservability,
  toastViewportProps,
  ...rest
}: BricksProviderProps) {
  useObservability = useObservability ?? defaultAnalyticsContext.useObservability;
  return (
    <AnalyticsContext.Provider
      value={{
        useObservability,
      }}
    >
      <TamaguiProvider config={config} defaultTheme="light" {...rest}>
        <PortalProvider>
          <ToastProvider>
            {children}
            <ToastViewport
              flexDirection="column"
              top={0}
              left={0}
              right={0}
              {...toastViewportProps}
            />
          </ToastProvider>
        </PortalProvider>
      </TamaguiProvider>
    </AnalyticsContext.Provider>
  );
}
